import { Button, Layout } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import CookiesPolicyService from '../../../services/CookiePolicy.service';
import { ROUTER } from '../../../constants/router';
import ListPrivacy from '../Shared/ListPrivacy';
import useNotify from '../../../context/Notification/useNotify';
import { useForm } from 'antd/lib/form/Form';
import DocTabs from './components/DocTabs';
import { usePreviewtoString } from './components/PreviewtoString';
import { PlusOutlined } from '@ant-design/icons';
import GlobalService from '../../../services/Global.service';
import CookiePolicyService from '../../../services/CookiePolicy.service';
import { POLICY_NAME } from '../POLICY';
import { calculateImageWidth } from '../../../util/util';
import usePermission from '../../../hooks/usePermission';

const CookiesPolicy = ({ selectType }) => {
    const { t, i18n } = useTranslation([
        'translation',
        'hr-policy',
        'cookies-policy',
    ]);
    let lang = i18n.language;
    const [data, setData] = useState([]);
    const [currentSelectList, setCurrentSelectList] = useState();
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState();
    const [detailPublished, setDetailPublished] = useState();

    let navigate = useNavigate();
    // Logo
    const [logo, setLogo] = useState('');
    const [logoPublish, setLogoPublish] = useState('');
    const [cookieList, setCookieList] = useState();
    const [cookieListPublished, setCookieListPublished] = useState();

    const getImage = async (companyLogoId) => {
        try {
            const { data } = await GlobalService.getImage(companyLogoId);
            if (data.resultData?.imageId) {
                return `data:image/png;base64,${data.resultData?.imageId}`;
            }
        } catch (error) {
            setLogo('');
            setLogoPublish('');
            console.log(error);
        }
    };

    // get cookie list
    const getCookieList = async (domainId) => {
        const { data } = await CookiePolicyService.getCookieDomain(domainId);
        const res = data?.resultData;
        return res;
    };

    const getPolicyList = async () => {
        const query = selectType ? selectType : '';
        const { data } = await CookiesPolicyService.searchCookiePolicy(query);
        if (data.resultData) {
            setData(data.resultData);
            setCurrentSelectList(data.resultData[0]);
        }
    };

    useEffect(() => {
        const getPolicyNotice = async () => {
            setLoading(true);
            try {
                const { data } =
                    await CookiesPolicyService.getCookiePolicyDetail(
                        currentSelectList?.cookiePolicyId
                    );
                const res = data?.resultData;
                if (res) {
                    setDetail(res);
                    setLoading(false);
                    // Logo
                    setLogo('');
                    if (res?.cookiePolicy?.cookieCompanyLogoId) {
                        getImage(res?.cookiePolicy?.cookieCompanyLogoId).then(
                            (logo) => setLogo(logo)
                        );
                    }
                    // get cookie list
                    const list = await getCookieList(
                        res?.cookiePolicy?.domainId
                    );
                    setCookieList(list);
                } else setLoading(false);
            } catch (error) {
                console.log(error);
                setLoading(false);
            }
        };
        const getPublishedVersion = async () => {
            try {
                const { data } = await CookiesPolicyService.getPublishedVersion(
                    currentSelectList?.cookiePolicyId
                );
                const res = data?.resultData;
                if (res) {
                    const parse = JSON.parse(res?.data);
                    setDetailPublished(parse);
                    // Logo
                    setLogoPublish('');
                    if (parse?.cookiePolicy?.cookieCompanyLogoId) {
                        getImage(parse?.cookiePolicy?.cookieCompanyLogoId).then(
                            (logo) => setLogoPublish(logo)
                        );
                    }
                    // get cookie list
                    const list = await getCookieList(
                        parse?.cookiePolicy?.domainId
                    );
                    setCookieListPublished(list);
                }
            } catch (error) {
                console.log(error);
            }
        };
        if (currentSelectList?.cookiePolicyId) getPolicyNotice();
        if (
            currentSelectList?.hasChanged &&
            currentSelectList?.status === 'Published'
        )
            getPublishedVersion();
        else setDetailPublished();
    }, [currentSelectList]);

    useEffect(() => {
        getPolicyList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectType]);

    const notify = useNotify();
    const [form] = useForm();

    const handelRename = (values) => {
        setLoading(true);
        if (currentSelectList?.cookiePolicyId) {
            const payload = {
                cookiePolicyId: currentSelectList?.cookiePolicyId,
                nameEn: values.nameEn,
                nameTh: values.nameTh,
            };
            setTimeout(async () => {
                try {
                    const { data } = await CookiesPolicyService.rename(payload);
                    if (data.resultCode === 1) {
                        form.resetFields();
                        setTimeout(() => {
                            setLoading(false);
                            getPolicyList();
                        }, 1300);
                    } else {
                        setLoading(false);
                        notify({
                            type: 'error',
                            title: t('common.Fail'),
                            description: t('Failed to rename'),
                        });
                    }
                } catch (error) {
                    setLoading(false);
                    notify({
                        type: 'error',
                        title: t('common.Fail'),
                        description: t('Failed to rename'),
                    });
                }
            }, 1000);
        }
    };

    const width1 = calculateImageWidth(logo);
    const width2 = calculateImageWidth(logoPublish);

    const { html } = usePreviewtoString(detail, 'TH', logo, cookieList, width1);
    const { html: htmlEn } = usePreviewtoString(
        detail,
        'EN',
        logo,
        cookieList,
        width1
    );

    const { html: htmlPublish } = usePreviewtoString(
        detailPublished,
        'TH',
        logoPublish,
        cookieListPublished,
        width2
    );
    const { html: htmlPublishEn } = usePreviewtoString(
        detailPublished,
        'EN',
        logoPublish,
        cookieListPublished,
        width2
    );

    const { isAuditor } = usePermission();

    return (
        <Layout>
            <div className="flex items-center my-4">
                <div className="flex-1 font-bold text-xl">
                    {t('Cookie Policy list', {
                        ns: POLICY_NAME.COOKIES_POLICY,
                    })}
                </div>
                <Button
                    disabled={isAuditor}
                    type="primary"
                    size="large"
                    onClick={() => {
                        navigate(`${ROUTER.POLICY.COOKIE_PRIVACY}/new#step1`);
                    }}
                    icon={<PlusOutlined />}
                >
                    {t('Add Policy')}
                </Button>
            </div>
            <div className="grid grid-cols-7 gap-4">
                <ListPrivacy
                    data={data}
                    pathEdit={ROUTER.POLICY.COOKIE_PRIVACY}
                    stepEdit={'#step6'} // step preview
                    currentSelectName={{
                        nameTh: currentSelectList?.cookiePolicyNameTh,
                        nameEn: currentSelectList?.cookiePolicyNameEn,
                        placeholderNameEN: `${t(
                            'cookies-policy.Name of Cookies Policy',
                            {
                                ns: 'cookie-policy',
                            }
                        )} (EN)`,
                        placeholderNameTH: `${t(
                            'cookies-policy.Name of Cookies Policy',
                            {
                                ns: 'cookie-policy',
                            }
                        )} (TH)`,
                    }}
                    linkName="external-cookie-policy"
                    getPolicyList={getPolicyList}
                    setCurrentSelectList={setCurrentSelectList}
                    currentSelectList={currentSelectList}
                    form={form}
                    // Parameters Name
                    policyObject={{
                        privacyName:
                            lang === 'en'
                                ? 'cookiePolicyNameEn'
                                : 'cookiePolicyNameTh',
                        nameTh: 'cookiePolicyNameTh',
                        nameEn: 'cookiePolicyNameEn',
                        privacyType:
                            lang === 'en' ? 'domainName' : 'domainName',
                        privacyId: 'cookiePolicyId',
                        privacyCompany:
                            lang === 'en'
                                ? 'cookieCompanyNameEn'
                                : 'cookieCompanyNameTh',
                    }}
                    handelRename={(value) => handelRename(value)}
                    historyObject={{
                        serviceHistory: CookiesPolicyService.getHistories,
                        type: 'cookie',
                        historyParamName: 'cookiePolicyId', // ชื่อ payload ของ id ที่ service get history เช่น hrPrivacyPolicyId: "1234" ที่ต้องใส่คือ: hrPrivacyPolicyId
                        historyResultIdName: 'cookiePolicyHistoryId', // ได้หลังจาก service get history ใน result ชื่อ parameter ของ id เช่น { "hrPrivacyPolicyHistoryId" : 1234 } ที่ต้องใส่คือ: hrPrivacyPolicyHistoryId
                    }}
                    deleteObject={{
                        serviceDelete: CookiesPolicyService.deleteCookiePolicy,
                        title:
                            currentSelectList?.status === 'Draft'
                                ? t('ModalTitleDeleteDraft', {
                                      ns: 'hr-policy',
                                  })
                                : t('ModalTitleDeleteDraft', {
                                      ns: 'hr-policy',
                                  }),
                        text:
                            currentSelectList?.status === 'Draft'
                                ? `${t('ModalDeleteDraft', {
                                      version: currentSelectList?.version,
                                      ns: 'hr-policy',
                                  })}`
                                : `${t('ModalDetailDeletePublish', {
                                      ns: 'cookies-policy',
                                  })} \n${t('ModalDeletePublish', {
                                      version:
                                          currentSelectList?.latestPublishVersion,
                                      ns: 'hr-policy',
                                  })}`,
                    }}
                    // pop up ของ confirm ก่อน delete
                    confirmObject={{
                        title:
                            lang === 'en'
                                ? 'There may be websites that use this cookie policy.'
                                : 'อาจมีเว็บไซต์ใช้นโยบายคุกกี้นี้อยู่',
                        text:
                            lang === 'en'
                                ? `Before deleting the data, please check that no website is using this cookie policy.`
                                : `ก่อนลบข้อมูล โปรดตรวจสอบว่าไม่มีเว็บไซต์ใดใช้นโยบายคุกกี้นี้`,
                    }}
                    loading={loading}
                    templateBody={{
                        html,
                        htmlEn,
                        htmlPublish,
                        htmlPublishEn,
                    }}
                >
                    {(previewLang) => (
                        <DocTabs
                            logo={logo}
                            logoPublish={logoPublish}
                            detail={detail}
                            loading={loading}
                            currentSelectList={currentSelectList}
                            previewLang={previewLang}
                            privacyPolicyNoticeId={
                                currentSelectList?.cookiePolicyId
                            }
                            detailPublished={detailPublished}
                            cookieList={cookieList}
                            cookieListPublished={cookieListPublished}
                        />
                    )}
                </ListPrivacy>
            </div>
        </Layout>
    );
};

export default CookiesPolicy;
